<template>
  <div class="d-flex align-items-center justify-content-center">
    <div class="mr-2">
      <v-icon color="white">mdi-eye</v-icon>
      <span class="text-white">Vista</span>
    </div>

    <v-progress-circular
      v-if="isLoading"
      indeterminate
      size="20"
      color="white"
    />

    <v-select
      v-else
      v-model="activeRouteName"
      :items="navigationItems"
      class="py-1 max-wx-250"
      dense
      hide-details="true"
      item-text="label"
      item-value="routeName"
      rounded
      solo
      @input="onNavigate"
    >
      <template
        v-if="showTabs && $route.meta.role !== 'Customer'"
        #prepend-item
      >
        <v-btn
          class="mr-2"
          :color="selectedUserTab.color"
          rounded
          small
          @click="activeTab = NavigationTabs.SELECTED_USER"
        >
          <img :src="selectedUserTab.icon" class="pr-1" aria-hidden />
          {{ selectedUserTab.label }}
        </v-btn>
        <v-btn
          :color="authUserTab.color"
          rounded
          small
          @click="activeTab = NavigationTabs.AUTH_USER"
        >
          <img :src="authUserTab.icon" class="pr-1" aria-hidden />
          {{ authUserTab.label }}
        </v-btn>
        <v-divider class="my-2" />
      </template>
    </v-select>
  </div>
</template>

<script>
  import { getUserRoutes, findActiveRoute } from './navigationRoutes.js';
  import { Channels } from '@/core/constants/channelConstants.js';
  import { roleLabels } from '@/core/constants/roleLabels.js';
  import idCardHold from '@/assets/icons/id-card-hold.svg';
  import idCard from '@/assets/icons/id-card.svg';

  import { mapGetters, mapActions } from 'vuex';
  import axios from '@/plugins/axios';

  const NavigationTabs = {
    AUTH_USER: 'authUser',
    SELECTED_USER: 'selectedUser',
  };

  const ChannelColors = {
    [Channels.RETAIL]: 'red-button',
    [Channels.WHOLESALER]: 'blue-button',
  };

  const SwitchChannelRoute = {
    ResultsExpressSales: Channels.RETAIL,
    ResultsExpressSalesPR: Channels.WHOLESALER,
  };

  const RouteQueries = {
    backordersHistory: { filter: 'all' },
    backordersHistoryManager: { filter: 'all' },
    backordersHistorySeller: { filter: 'all' },
    backordersHistoryRegional: { filter: 'all' },
  };

  export default {
    name: 'NavigationSelect',
    props: {
      currentCode: {
        type: Number || String,
        required: true,
      },
    },
    data: () => ({
      NavigationTabs,
      isLoading: false,
      showTabs: false,
      activeTab: NavigationTabs.SELECTED_USER,
      activeRouteName: null,
      selectedUserRole: null,
    }),

    computed: {
      ...mapGetters({
        user: 'auth/user',
        userChannel: 'auth/channel',
        previewChannel: 'auth/previewNavigationChannel',
      }),
      isViewingOtherUser() {
        return (
          this.currentCode &&
          parseInt(this.currentCode) !== parseInt(this.user.employee_number) &&
          this.$route.meta.role !== 'Customer'
        );
      },
      isSelectedUserTabActive() {
        return this.activeTab === NavigationTabs.SELECTED_USER;
      },
      isAuthUserTabActive() {
        return this.activeTab === NavigationTabs.AUTH_USER;
      },
      activeRole() {
        return this.activeTab === NavigationTabs.SELECTED_USER
          ? this.selectedUserRole
          : this.user?.roles?.[0]?.name;
      },
      activeChannel() {
        return this.previewChannel ?? this.userChannel;
      },
      navigationItems() {
        return getUserRoutes({
          role: this.activeRole,
          channel: this.activeChannel,
          context: {},
        });
      },
      authUserTab() {
        return {
          color: this.isAuthUserTabActive
            ? ChannelColors[this.userChannel]
            : 'grey-button',
          icon: this.isAuthUserTabActive ? idCard : idCardHold,
          label: roleLabels[this.user?.roles?.[0]?.name] ?? 'Desconocido',
        };
      },
      selectedUserTab() {
        return {
          color: this.isSelectedUserTabActive
            ? ChannelColors[this.userChannel]
            : 'grey-button',
          icon: this.isSelectedUserTabActive ? idCard : idCardHold,
          label: this.selectedUserRole ?? 'Desconocido',
        };
      },
    },
    watch: {
      $route(to) {
        if (to.name !== 'Logout') {
          this.init();
        }
      },
    },
    mounted() {
      this.init();
    },
    methods: {
      ...mapActions({ updateUserChannel: 'auth/actualizarUsuario' }),
      async init() {
        this.isLoading = true;
        this.showTabs = false;
        this.activeTab = NavigationTabs.SELECTED_USER;
        this.selectedUserRole = null;

        await this.initSelectedUser();

        this.syncActiveRoute();
        this.isLoading = false;
      },
      async initSelectedUser() {
        if (this.isViewingOtherUser) {
          this.showTabs = true;
          this.selectedUserRole = await this.fetchSelectedUserRole();
        } else {
          this.selectedUserRole = this.user?.roles?.[0]?.name;
        }
      },
      async fetchSelectedUserRole() {
        try {
          const { data } = await axios.get('/users/role', {
            params: { employee_number: this.currentCode },
            cachable: true,
          });

          return data.data || null;
        } catch {
          this.$toast.error('Error al cargar el menú de roles');
          return null;
        }
      },
      syncActiveRoute() {
        const matched = findActiveRoute(this.navigationItems, this.$route.name);
        this.activeRouteName = matched?.routeName ?? null;
      },
      ...mapActions({ updateUserChannel: 'auth/actualizarUsuario' }),
      onNavigate() {
        const routeName = this.activeRouteName;
        const channel = SwitchChannelRoute[routeName] ?? this.activeChannel;
        const query = RouteQueries[routeName]
          ? { ...this.$route.query, ...RouteQueries[routeName] }
          : undefined;

        const navigate = () => {
          this.$store.commit('auth/SET_PREVIEW_NAVIGATION_CHANNEL', null);
          localStorage.setItem('channel', channel);
          this.$router.push({
            name: routeName,
            ...(query && { query }),
          });
        };

        const isChannelSelection =
          this.$route.name === 'DirectorChannelSelection';
        const channelChanged = channel !== this.userChannel;

        if (isChannelSelection || channelChanged) {
          this.updateUserChannel(channel).then(navigate);
        } else {
          navigate();
        }
      },
    },
  };
</script>
